/**
 * Dates and times, in the user's timezone
 *
 * Fantasy runs on deadlines, so "Sunday 1pm" has to mean the user's Sunday 1pm
 * — not the browser's guess, and definitely not the server's. Every visible
 * date in the app should go through here.
 *
 * Every function takes the timezone explicitly rather than reaching for global
 * state, so they stay easy to test and impossible to get subtly wrong by
 * rendering before the user has loaded. Pass `user?.timezone`; a null falls
 * back to whatever the browser reports, which is the right answer for anyone
 * who has never opened Settings.
 *
 * Dates are written "15 January 2026" on purpose — unambiguous to both AU and
 * US readers, unlike 01/15 or 15/01, which mean different days depending on
 * who's reading. JAI's users are mostly American; the business is Australian.
 */

/** The browser's own guess. Used whenever the user hasn't chosen one. */
export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/**
 * Build formatter options with the timezone applied.
 *
 * An invalid or unknown zone must never take a page down — a stale saved value
 * (a zone that got renamed, say) falls back to the browser rather than
 * throwing inside a render.
 */
const withZone = (options, timezone) => {
  const zone = timezone || browserTimezone()
  if (!zone) return options
  try {
    // Cheap probe: Intl throws here rather than at format time.
    new Intl.DateTimeFormat('en-GB', { timeZone: zone })
    return { ...options, timeZone: zone }
  } catch {
    return options
  }
}

const toDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "15 January 2026" */
export const formatDate = (value, timezone) => {
  const date = toDate(value)
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', withZone(
    { day: 'numeric', month: 'long', year: 'numeric' },
    timezone,
  ))
}

/** "15 Jan" — for dense lists where the year is noise. */
export const formatShortDate = (value, timezone) => {
  const date = toDate(value)
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', withZone(
    { day: 'numeric', month: 'short' },
    timezone,
  ))
}

/**
 * "15 January 2026 at 7:30 pm" — for anything deadline-shaped.
 *
 * hour12 is forced on: en-GB would otherwise render 19:30, and JAI's users are
 * overwhelmingly American, where a 24-hour lineup lock reads as a typo.
 */
export const formatDateTime = (value, timezone) => {
  const date = toDate(value)
  if (!date) return '—'
  return date.toLocaleString('en-GB', withZone(
    { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
    timezone,
  ))
}

/** "Mon 3:45 pm" — a compact clock, used to show a timezone choice is right. */
export const formatClock = (value, timezone) => {
  const date = toDate(value) || new Date()
  return date.toLocaleString('en-US', withZone(
    { weekday: 'short', hour: 'numeric', minute: '2-digit' },
    timezone,
  ))
}

/**
 * "Updated 2 hours ago" — a relative read on a cache/generated timestamp.
 * Deliberately timezone-free: it's a diff against Date.now(), so "2 hours
 * ago" reads the same everywhere. Shared by Trade Radar's status line and
 * Opponent Scout's "Updated" line — was duplicated in both before.
 */
export function timeAgo(iso) {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.round(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

/**
 * The hour of day (0–23) as the USER experiences it right now.
 *
 * This is what makes "Good evening" correct for someone in New York while the
 * server sits in Sydney the following morning. Reading `getHours()` off a Date
 * would give the browser's hour, which is only right by luck for anyone who
 * has set a timezone different from the device they're holding.
 */
export const currentHourIn = (timezone) => {
  const zone = timezone || browserTimezone()
  try {
    const hour = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', hour12: false, ...(zone ? { timeZone: zone } : {}),
    }).format(new Date())
    const parsed = Number(hour)
    // "24" is a legal hour-cycle output for midnight in some environments.
    return Number.isFinite(parsed) ? parsed % 24 : new Date().getHours()
  } catch {
    return new Date().getHours()
  }
}
